// One album, grouped by the person's own history rather than by date:
// Your favourites (make again, or cooked 3+ times) · Not tried yet · Cooked before.
import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { api, ApiError } from '../api';
import { Link, navigate } from '../router';
import { Back, Bag, Check, Pin, VegMark } from '../icons';
import { BottomNav, ErrorBox, GroceryFab, useToast } from '../components/Shell';
import { SwipeToGrocery } from '../components/Swipe';
import { SurpriseBox } from '../components/Covers';
import { NameSheet } from '../components/AlbumSheets';
import { reelThumb, Thumb } from '../components/Thumb';
import { albumNames, skinFor } from '../albums';
import { namesFor } from '../lib/format';
import { setGroceryIDs, groceryIDs } from '../grocery-selection';
import { shortDateL, useT } from '../i18n';
import { useScript } from '../script';
import { load, save } from '../storage';
import type { AlbumView, SavedReel, Script } from '../types';

type Filter = 'all' | 'untried' | 'veg' | 'nonveg';

const isFav = (r: SavedReel) => r.verdict === 'again' || r.cooked_count >= 3;

export function Album({ kind, albumKey }: { kind: string; albumKey: string }) {
  const t = useT();
  const [script] = useScript();
  const ref = `${kind}:${albumKey}`;
  const cache = `er.cache.album.${ref}`;
  const [data, setData] = useState<AlbumView | null>(() => load<AlbumView | null>(cache, null));
  const [err, setErr] = useState('');
  const [missing, setMissing] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [shopping, setShopping] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, showToast] = useToast();

  const refresh = () =>
    api.album(ref).then((d) => { setData(d); save(cache, d); setErr(''); }).catch((e: ApiError) => {
      if (e.status === 404) setMissing(true);
      else if (e.status !== 401) setErr(e.message);
    });
  useEffect(() => { refresh(); }, [ref]);
  useEffect(() => {
    if (!confirmDelete) return;
    const id = setTimeout(() => setConfirmDelete(false), 5000);
    return () => clearTimeout(id);
  }, [confirmDelete]);

  if (missing) {
    return (
      <div class="page">
        <div class="backbar"><Link href="/albums" class="icon-btn" aria-label={t('album.backToAlbums')}><Back /></Link></div>
        <ErrorBox message={t('album.notFound')} />
      </div>
    );
  }
  if (!data) {
    return err
      ? <div class="page"><div class="backbar"><Link href="/albums" class="icon-btn" aria-label={t('album.backToAlbums')}><Back /></Link></div><ErrorBox message={err} onRetry={refresh} /></div>
      : <div class="page"><div class="skeleton" style="height:240px;border-radius:0 0 28px 28px" aria-label={t('common.loading')} /></div>;
  }

  const c = data.cover;
  const s = skinFor(c.ref);
  const n = albumNames(c, script);
  const mine = c.kind === 'mine';

  const togglePin = async () => {
    const next = !c.pinned;
    setData({ ...data, cover: { ...c, pinned: next } });
    try {
      await api.pin(c.ref, next);
      showToast(t(next ? 'album.pinned' : 'album.unpinned'));
    } catch (e) {
      setData({ ...data, cover: { ...c, pinned: !next } });
      showToast((e as ApiError).message);
    }
  };

  const shown = data.recipes.filter((r) => {
    switch (filter) {
      case 'untried': return r.cooked_count === 0;
      case 'veg': return r.is_veg === true;
      case 'nonveg': return r.is_veg === false;
      default: return true;
    }
  });
  const favs = shown.filter(isFav);
  const untried = shown.filter((r) => !isFav(r) && r.cooked_count === 0);
  const cooked = shown.filter((r) => !isFav(r) && r.cooked_count > 0);
  const cookable = data.recipes.filter((r) => r.status === 'ready' && r.ingredient_count > 0);

  const chip = (f: Filter, label: ComponentChildren) => (
    <button class="chip" style="height:40px" aria-pressed={filter === f} onClick={() => setFilter(filter === f && f !== 'all' ? 'all' : f)}>{label}</button>
  );

  return (
    <div class="page with-nav" style="padding-bottom:calc(160px + env(safe-area-inset-bottom))">
      <section class="hero" style={`background:${s.bg};color:${s.fg};border:${s.border}`}>
        <div class="bar">
          <Link href="/albums" aria-label={t('album.backToAlbums')}><Back /></Link>
          <button aria-pressed={c.pinned} aria-label={t(c.pinned ? 'album.unpin' : 'album.pin')} onClick={togglePin}
            style={c.pinned ? 'background:rgba(0,0,0,.12)' : ''}><Pin /></button>
        </div>
        <div class="kicker">{t(`kind.${c.kind}`)}</div>
        <h1>{n.primary}</h1>
        <div class="stats">{t('album.stats', { n: c.count, c: c.cooked, u: c.not_tried })}</div>
      </section>

      {data.recipes.length > 0 && (
        <div class="chips" role="group" aria-label={t('filter.label')} style="padding:16px 16px 0">
          {chip('all', t('filter.all'))}
          {chip('untried', t('filter.untried'))}
          {chip('veg', <><VegMark veg={true} size={13} />{t('common.veg')}</>)}
          {chip('nonveg', <><VegMark veg={false} size={13} />{t('common.nonveg')}</>)}
        </div>
      )}

      {cookable.length >= 2 && (
        <div style="padding:14px 16px 0"><SurpriseBox album={c.ref} label={t('album.pick')} /></div>
      )}

      {data.recipes.length === 0 && (
        <div class="hint-box" style="margin:18px 16px 0">{mine ? t('album.emptyMine') : t('album.empty')}</div>
      )}
      {data.recipes.length > 0 && shown.length === 0 && (
        <div class="hint-box" style="margin:18px 16px 0">
          {t('album.noMatch')} <button class="btn-text" style="padding:0;min-height:0;color:var(--accent-text)" onClick={() => setFilter('all')}>{t('album.showAll')}</button>
        </div>
      )}

      {favs.length > 0 && (
        <Group title={t('album.favourites')}>
          <div class="fav-grid">{favs.map((r) => <FavCard r={r} script={script} />)}</div>
        </Group>
      )}
      {untried.length > 0 && (
        <Group title={t('album.notTried')}>{untried.map((r) => <Swipeable r={r} onToast={showToast}><UntriedCard r={r} script={script} /></Swipeable>)}</Group>
      )}
      {cooked.length > 0 && (
        <Group title={t('album.cookedBefore')}><div class="rows">{cooked.map((r) => <Swipeable r={r} onToast={showToast}><CookedRow r={r} script={script} /></Swipeable>)}</div></Group>
      )}

      {cookable.length > 0 && (
        <div style="padding:22px 16px 0;display:flex;justify-content:center">
          <button class="btn btn-outline" style="min-height:48px" onClick={() => setShopping(true)}><Bag size={18} />{t('album.shop')}</button>
        </div>
      )}

      {mine && (
        <div style="padding:22px 16px 0;display:flex;flex-direction:column;align-items:center;gap:4px">
          <button class="btn-text" onClick={() => setRenaming(true)}>{t('album.rename')}</button>
          <button class="btn-danger-text" onClick={async () => {
            if (!confirmDelete) { setConfirmDelete(true); return; }
            try { await api.deleteAlbum(Number(c.key)); navigate('/albums', { replace: true }); } catch (e) { showToast((e as ApiError).message); }
          }}>{confirmDelete ? t('album.deleteConfirm') : t('album.delete')}</button>
        </div>
      )}

      {shopping && <ShopSheet recipes={cookable} script={script} onClose={() => setShopping(false)} />}
      {renaming && <NameSheet initial={c.name} albumId={Number(c.key)} onClose={() => setRenaming(false)}
        onSaved={(name) => { setRenaming(false); setData({ ...data, cover: { ...c, name: name as string } }); }} />}
      {toast}
      <GroceryFab />
      <BottomNav current="albums" />
    </div>
  );
}

/** Ready recipes with ingredients can be swiped onto the grocery list. */
function Swipeable({ r, onToast, children }: { r: SavedReel; onToast: (m: string) => void; children: ComponentChildren }) {
  if (r.status !== 'ready' || r.ingredient_count === 0) return <>{children}</>;
  return <SwipeToGrocery id={r.id} onToast={onToast}>{children}</SwipeToGrocery>;
}

function Group({ title, children }: { title: string; children: ComponentChildren }) {
  return (
    <section style="padding:22px 16px 0;display:flex;flex-direction:column;gap:10px">
      <h2 class="section-label" style="margin:0 4px">{title}</h2>
      {children}
    </section>
  );
}

function names(r: SavedReel, script: Script) {
  return namesFor({ ta: r.dish_name_ta, en: r.dish_name_en, tg: r.dish_name_translit }, script);
}

function FavCard({ r, script }: { r: SavedReel; script: Script }) {
  const t = useT();
  const [primary, secondary] = names(r, script);
  return (
    <Link href={`/r/${r.id}`} class="card album-card">
      <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="top" />
      <span class="card-meta"><VegMark veg={r.is_veg} size={13} unknown />{r.cuisine && <span>{r.cuisine}</span>}</span>
      <span class="ta-name clamp3" style="font-weight:700;font-size:17px" lang={script === 'ta' ? 'ta' : undefined}>{primary}</span>
      {secondary && <span class="clamp2" style="font-size:13px;color:var(--muted)">{secondary}</span>}
      <span class="pill" style="align-self:flex-start;margin-top:4px">{t('n.cooked', { n: r.cooked_count })}</span>
    </Link>
  );
}

function UntriedCard({ r, script }: { r: SavedReel; script: Script }) {
  const t = useT();
  const [primary, secondary] = names(r, script);
  const meta = [r.cuisine, t('album.saved', { date: shortDateL(r.saved_at) })].filter(Boolean).join(' · ');
  return (
    <Link href={`/r/${r.id}`} class="card album-card" style="flex-direction:row;gap:14px;align-items:flex-start">
      <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="card" />
      <span style="flex:1;min-width:0;display:flex;flex-direction:column;gap:4px">
        <span class="card-meta"><VegMark veg={r.is_veg} size={13} unknown /><span>{meta}</span></span>
        <span class="ta-name" style="font-weight:700;font-size:18px;text-wrap:pretty" lang={script === 'ta' ? 'ta' : undefined}>{primary}</span>
        <span style="font-size:14px;color:var(--muted)">{[secondary, r.creator_handle && '@' + r.creator_handle].filter(Boolean).join(' · ')}</span>
      </span>
    </Link>
  );
}

function CookedRow({ r, script }: { r: SavedReel; script: Script }) {
  const t = useT();
  const [primary, secondary] = names(r, script);
  const right = r.verdict === 'ok' || r.verdict === 'no' ? `“${t(`verdict.${r.verdict}`)}”` : t('n.cooked', { n: r.cooked_count });
  return (
    <Link href={`/r/${r.id}`} class="row" style="justify-content:space-between">
      <Thumb src={reelThumb(r.id, r.has_thumb)} name={primary} size="row" />
      <span class="grow">
        <span class="ta-name" style="font-size:16px;font-weight:700" lang={script === 'ta' ? 'ta' : undefined}>{primary}</span>
        {secondary && <span class="sub">{secondary}</span>}
      </span>
      <span class="small muted" style="flex:none">{right}</span>
    </Link>
  );
}

/** Pick recipes from this album for the grocery list (merged with anything already there). */
function ShopSheet({ recipes, script, onClose }: { recipes: SavedReel[]; script: Script; onClose: () => void }) {
  const t = useT();
  const [picked, setPicked] = useState<number[]>(() => recipes.filter((r) => groceryIDs().includes(r.id)).map((r) => r.id));
  return (
    <div class="scrim" role="dialog" aria-modal="true" aria-labelledby="shop-title" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="sheet" style="max-height:85dvh">
        <div class="sheet-handle" aria-hidden="true" />
        <h2 id="shop-title" class="h2">{t('album.shopTitle')}</h2>
        <div class="rows" style="overflow-y:auto">
          {recipes.map((r) => {
            const on = picked.includes(r.id);
            return (
              <button class="row" aria-pressed={on} onClick={() => setPicked(on ? picked.filter((x) => x !== r.id) : [...picked, r.id])}>
                <span class="checkbox" style={on ? 'background:var(--accent);border-color:var(--ink)' : ''}>{on && <Check style="color:var(--on-accent)" />}</span>
                <span class="grow"><span class="ta-name" style="font-weight:700" lang={script === 'ta' ? 'ta' : undefined}>{names(r, script)[0]}</span></span>
              </button>
            );
          })}
        </div>
        <button class="btn btn-primary" disabled={picked.length === 0} onClick={() => {
          const keep = groceryIDs().filter((id) => !recipes.some((r) => r.id === id));
          setGroceryIDs([...keep, ...picked]);
          navigate('/grocery');
        }}>{t('album.shopGo', { n: picked.length })}</button>
        <button class="btn-text" onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}
